// יצירת קמפיין חדש מתוך כרטיס לקוח.
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { slugifyKey } from '@bb/schema';
import { api } from '../../api';
import { ErrorBox, Field, Modal } from '../ui';
import { KEY_HINT, KEY_RE, LANGS, useAction } from './common';
import { isUrl } from './LinksEditor';
import type { BrandRow } from './BrandPanel';

export function NewCampaignModal({ clientId, brands, onClose }: { clientId: string; brands: BrandRow[]; onClose: () => void }) {
  const nav = useNavigate();
  const [f, setF] = useState({ brandId: brands[0]?.id ?? '', name: '', campaignKey: '', keyTouched: false, startDate: '', endDate: '', language: 'he', goal: '', description: '', defaultUrl: '' });
  const set = (patch: Partial<typeof f>) => setF((x) => ({ ...x, ...patch }));
  const { busy, error, run } = useAction();
  const keyBad = f.campaignKey !== '' && !KEY_RE.test(f.campaignKey);
  const urlBad = f.defaultUrl !== '' && !isUrl(f.defaultUrl);
  const datesBad = !!(f.startDate && f.endDate && f.endDate < f.startDate);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (keyBad || urlBad || datesBad) return;
    const r = await run(() => api.post<{ campaign: { id: string } }>('/api/campaigns', {
      clientId, brandId: f.brandId, name: f.name, campaignKey: f.campaignKey, language: f.language, goal: f.goal, description: f.description,
      defaultUrl: f.defaultUrl, startDate: f.startDate || null, endDate: f.endDate || null,
    }), 'הקמפיין נוצר');
    if (r) nav(`/campaigns/${r.campaign.id}`);
  };
  if (!brands.length) {
    return <Modal title="קמפיין חדש" onClose={onClose}><div className="alert warn">כדי ליצור קמפיין צריך קודם מותג ללקוח (לשונית "מותגים").</div></Modal>;
  }
  return (
    <Modal title="קמפיין חדש" onClose={onClose} wide>
      <form className="stack" onSubmit={submit}>
        <div className="grid cols-2">
          <Field label="מותג *" hint="הקמפיין יירש snapshot של הגרסה הנוכחית">
            <select value={f.brandId} onChange={(e) => set({ brandId: e.target.value })} required>
              {brands.map((b) => <option key={b.id} value={b.id}>{b.name} (גרסה {b.current_version})</option>)}
            </select>
          </Field>
          <Field label="שם הקמפיין *">
            <input required value={f.name} onChange={(e) => set({ name: e.target.value, ...(f.keyTouched ? {} : { campaignKey: e.target.value ? slugifyKey(e.target.value) : '' }) })} />
          </Field>
          <Field label="מפתח קמפיין (utm_campaign) *" hint={KEY_HINT}>
            <input required className="ltr mono" aria-invalid={keyBad} value={f.campaignKey} onChange={(e) => set({ campaignKey: e.target.value, keyTouched: true })} style={{ borderColor: keyBad ? 'var(--danger)' : undefined }} />
          </Field>
          <Field label="שפה">
            <select value={f.language} onChange={(e) => set({ language: e.target.value })}>{Object.entries(LANGS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
          </Field>
          <Field label="תאריך התחלה"><input type="date" value={f.startDate} onChange={(e) => set({ startDate: e.target.value })} /></Field>
          <Field label="תאריך סיום"><input type="date" value={f.endDate} onChange={(e) => set({ endDate: e.target.value })} aria-invalid={datesBad} /></Field>
        </div>
        {keyBad && <div className="small" style={{ color: 'var(--danger)' }}>מפתח לא תקין — {KEY_HINT}</div>}
        {datesBad && <div className="small" style={{ color: 'var(--danger)' }}>תאריך הסיום לפני תאריך ההתחלה</div>}
        <Field label="URL ברירת מחדל (דף נחיתה)"><input className="ltr" placeholder="https://" value={f.defaultUrl} onChange={(e) => set({ defaultUrl: e.target.value })} aria-invalid={urlBad} /></Field>
        {urlBad && <div className="small" style={{ color: 'var(--danger)' }}>כתובת לא תקינה</div>}
        <Field label="מטרה"><textarea value={f.goal} onChange={(e) => set({ goal: e.target.value })} style={{ minHeight: 60 }} /></Field>
        <Field label="תיאור / בריף"><textarea value={f.description} onChange={(e) => set({ description: e.target.value })} /></Field>
        <ErrorBox error={error} />
        <div className="row"><button className="btn primary" disabled={busy || !f.name || !f.campaignKey || keyBad || urlBad || datesBad}>{busy ? 'יוצר…' : 'יצירת קמפיין'}</button><button type="button" className="btn" onClick={onClose}>ביטול</button></div>
      </form>
    </Modal>
  );
}
